import { centerOfMass } from "@turf/turf";
import { LngLat, MapGeoJSONFeature, MapLayerMouseEvent, MapSourceDataEvent } from "maplibre-gl";
import { useCallback, useEffect, useMemo, useState } from "react";
import { MapRef, useMap } from "react-map-gl/maplibre";

import { removeNullUndefined } from "@nens/shared-utils";

import { VECTOR_LAYER_ID } from "@/config/map-layer-constants";
import { getVectorLayers } from "@/lib/rana-map/components/rana-map-vectors";
import { VectorDescriptorLayer } from "@/utils/get-vector-descriptor-layers-and-sprite";

const getVectorFeaturesAtPoint = (map: MapRef, point: LngLat) => {
  const BUFFER_METERS = 10;
  const { x, y } = map.project(point);

  // Unproject a neighboring pixel to measure how many meters one pixel represents at this location/zoom
  const metersPerPixel = point.distanceTo(map.unproject([x + 1, y]));
  const bufferPx = Math.min(BUFFER_METERS / metersPerPixel, 50);

  const features = map
    .queryRenderedFeatures([
      [x - bufferPx, y - bufferPx],
      [x + bufferPx, y + bufferPx],
    ])
    .filter(({ sourceLayer }) => sourceLayer === VECTOR_LAYER_ID) // Filter out all layers that are not from our backend (so all maptiler layers)
    .sort((a, b) => {
      const centroidA = map.project(centerOfMass(a).geometry.coordinates as [number, number]);
      const centroidB = map.project(centerOfMass(b).geometry.coordinates as [number, number]);
      const distA = Math.hypot(centroidA.x - x, centroidA.y - y);
      const distB = Math.hypot(centroidB.x - x, centroidB.y - y);
      return distA - distB;
    });

  return features.length > 0 ? features : null;
};

const getFeatureSourcesKey = (features: MapGeoJSONFeature[]) =>
  features.map((feature) => feature.source).join(",");

const hasSameFeatureSources = (
  nextFeatures: MapGeoJSONFeature[],
  previousFeatures: MapGeoJSONFeature[]
) => getFeatureSourcesKey(nextFeatures) === getFeatureSourcesKey(previousFeatures);

export type VectorInfoItem = {
  title: string;
  features: MapGeoJSONFeature[];
  descriptorId: string;
  layerName: string;
};

type UseClickedOnVectorMapReturn = {
  clearMapClick: () => void;
  clickedPoint: LngLat | null; // The point where the user initially clicked. If the user is looking at a non-vector in the popup, point will be set to this.
  point: LngLat | null; // The point that the popup currently corresponds to. Will be the center of a feature on vectors.
  setPoint: (point: LngLat | null) => void;
  vectorInfoItems: VectorInfoItem[]; // The features at the clicked point, grouped by layer, to be shown in the popup
};

/* Track where the user clicked on the map, both its LngLat and possible features. */
export function useClickedOnVectorMap(
  mapId: string,
  vectorDescriptorLayers: VectorDescriptorLayer[]
): UseClickedOnVectorMapReturn {
  const vectorLayers = getVectorLayers(vectorDescriptorLayers);
  const { [mapId]: map } = useMap();

  const [clickedPoint, setClickedPoint] = useState<LngLat | null>(null);
  const [point, setPoint] = useState<LngLat | null>(null);

  const [selectedFeatures, setSelectedFeatures] = useState<MapGeoJSONFeature[] | null>(null);

  useEffect(
    function syncSelectedFeaturesOnMapChanges() {
      if (!map || !clickedPoint) return;

      const refreshSelectedFeatures = () => {
        const features = getVectorFeaturesAtPoint(map, clickedPoint);

        if (!features) return setSelectedFeatures(null);

        // Only update if the feature set changed to avoid update loops.
        if (hasSameFeatureSources(features, selectedFeatures ?? [])) return;

        setSelectedFeatures(features);
      };

      const onSourceDataChange = (e: MapSourceDataEvent) => {
        // If newly loaded vector data changes features at the clicked point,
        // refresh popup content.
        if (!e.isSourceLoaded || e.source.type !== "vector" || e.sourceDataType === "idle") return;
        refreshSelectedFeatures();
      };

      const scheduleRefreshAfterStyleChange = () => {
        setTimeout(refreshSelectedFeatures);
      };

      map.on("zoomend", refreshSelectedFeatures);
      map.on("sourcedata", onSourceDataChange);
      map.on("styledata", scheduleRefreshAfterStyleChange); // Fires when layer visibility changes

      return () => {
        map.off("zoomend", refreshSelectedFeatures);
        map.off("sourcedata", onSourceDataChange);
        map.off("styledata", scheduleRefreshAfterStyleChange);
      };
    },
    [map, clickedPoint, selectedFeatures]
  );

  useEffect(() => {
    if (!map) return;
    const handleMapClick = (event: MapLayerMouseEvent) => {
      if (!map) return;
      setClickedPoint(event.lngLat);
      setPoint(event.lngLat);
      setSelectedFeatures(getVectorFeaturesAtPoint(map, event.lngLat));
    };

    map.on("click", handleMapClick);
    return () => {
      map.off("click", handleMapClick);
    };
  }, [map]);

  const clearMapClick = useCallback(() => {
    setClickedPoint(null);
    setPoint(null);
    setSelectedFeatures(null);
  }, []);

  const vectorInfoItems: VectorInfoItem[] = useMemo(
    () =>
      removeNullUndefined(
        vectorLayers.map(({ title, layerId, descriptorId, layerName }) => {
          if (!selectedFeatures) return null;
          const features = selectedFeatures.filter(
            (feature) => (feature.layer?.id ?? "") === layerId
          );
          return features.length > 0 ? { title, features, descriptorId, layerName } : null;
        })
      ),
    [selectedFeatures, vectorLayers]
  );

  return {
    clearMapClick,
    clickedPoint,
    point,
    setPoint,
    vectorInfoItems,
  };
}
